import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SessionService } from '@monsido/core/session/session.service';
import { PageAccessibilityRepoService } from '@client/app/services/api/page-accessibility/page-accessibility-repo.service';
import { AccessibilityFiltersService } from '@client/app/modules/accessibility/services/filters/accessibility-filters.service';

interface TabOption {
    name: string;
    value: string;
}

interface Issues {
    A?: any[];
    AA?: any[];
    AAA?: any[];
}

@Component({
    selector: 'page-accessibility-wcag2-0',
    standalone: false,
    template: `
        <div class="row mr-lg-0 max-height align-items-stretch">
            <div class="col-lg-30 pr-lg-0">
                <div class="scroll max-height d-flex flex-column">
                    <page-details-sections-tabs class="pl-3" [tab]="tab" [options]="tabOptions" (onTabChange)="handleTabChange($event)">
                        <li role="menuitem" [attr.aria-label]="'Filter' | translate">
                            <filter-button
                                [showFiltersBtn]="true"
                                [(isFiltersOpen)]="isFiltersOpen">
                            </filter-button>
                        </li>
                        <li role="menuitem"
                            *ngIf="showIgnoredChecks"
                            [class.active]="tab == 'ignored_issues'">
                            <a role="button"
                               class="py-2 px-2 text-link text-nowrap inline-block"
                               (click)="onTabChange('ignored_issues')"
                               [attr.aria-label]="'Ignored checks' | translate">
                                <span class="fas fa-eye-slash"></span>
                                <span translate>Ignored checks</span>
                            </a>
                        </li>
                        <li role="menuitem"
                            [class.active]="tab == 'passed_checks'">
                            <a role="button"
                               class="py-2 px-2 text-link text-nowrap inline-block"
                               [attr.aria-label]="'Passed checks' | translate"
                               (click)="onTabChange('passed_checks')">
                                <span class="fas fa-check"></span>
                                <span translate>Passed checks</span>
                            </a>
                        </li>
                    </page-details-sections-tabs>
                    <div class="flex-grow-1">
                        <mon-spinner [loading]="loading">
                            <div class="pt-5" *ngIf="isFiltersOpen">
                                <table-filters
                                    [(open)]="isFiltersOpen"
                                    [(ngModel)]="filters"
                                    name="filters"
                                    [filters]="availableFilters"
                                    [selectorItemTemplate]="'app/pages/accessibility/templates/table-filter-selector-item.html'"
                                    (onChanges)="onFiltersChange($event)">
                                </table-filters>
                            </div>
                            <page-accessibility-wcag2-0-table
                                *ngIf="issues.A && issues.A.length > 0"
                                [active]="active"
                                [tab]="tab"
                                iconColor="wcag-a"
                                headerIcon="fa-mon-wcag-a"
                                [header]="'Level A accessibility checks' | translate"
                                (selectCheck)="onSelectCheck($event.check)"
                                [check]="check"
                                [progress]="progress"
                                [issues]="issues.A">
                            </page-accessibility-wcag2-0-table>

                            <page-accessibility-wcag2-0-table
                                *ngIf="issues.AA && issues.AA.length > 0"
                                [active]="active"
                                [tab]="tab"
                                iconColor="wcag-aa"
                                headerIcon="fa-mon-wcag-aa"
                                [header]="'Level AA accessibility checks' | translate"
                                (selectCheck)="onSelectCheck($event.check)"
                                [check]="check"
                                [progress]="progress"
                                [issues]="issues.AA">
                            </page-accessibility-wcag2-0-table>

                            <page-accessibility-wcag2-0-table
                                *ngIf="issues.AAA && issues.AAA.length > 0"
                                [active]="active"
                                [tab]="tab"
                                iconColor="wcag-aaa"
                                headerIcon="fa-mon-wcag-aaa"
                                [header]="'Level AAA accessibility checks' | translate"
                                (selectCheck)="onSelectCheck($event.check)"
                                [check]="check"
                                [progress]="progress"
                                [issues]="issues.AAA">
                            </page-accessibility-wcag2-0-table>
                        </mon-spinner>
                    </div>
                </div>
            </div>
            <div class="col-lg-18 px-lg-0 information-shadow bg-light-grey">
                <div class="scroll max-height">
                    <mon-spinner [loading]="errorLoading">
                        <page-details-section-accessibility-information
                            *ngIf="check"
                            (refreshErrors)="getErrors(true)"
                            [page]="page"
                            (refreshPage)="refreshPage.emit()"
                            [check]="check">
                        </page-details-section-accessibility-information>
                    </mon-spinner>
                </div>
            </div>
        </div>
    `,
})
export class PageAccessibilityWcag20Component implements OnInit {
    @Input() availableFilters: any;
    @Input() filters: any = {};
    @Input() issues: Issues = {};
    @Input() progress: any;
    @Input() loading: boolean = false;
    @Input() showIgnoredChecks: boolean = false;
    @Input() tab: string = '';
    @Input() page: any;
    @Input() active: any;

    @Output() refreshPage = new EventEmitter<void>();
    @Output() tabChange = new EventEmitter<string>();
    @Output() refreshChecks = new EventEmitter<void>();

    isFiltersOpen: boolean = false;
    tabOptions: TabOption[] = [];
    check: any;
    errorLoading: boolean = false;
    errorProgress: Promise<any> | null = null;

    constructor (
        private translateService: TranslateService,
        private sessionService: SessionService,
        private pageAccessibilityRepo: PageAccessibilityRepoService,
        private accessibilityFiltersService: AccessibilityFiltersService,
    ) {}

    ngOnInit (): void {
        this.isFiltersOpen = false;
        this.setupTabs();
    }

    onFiltersChange (changes: any): void {
        if (changes.type && changes.type.length > 0) {
            const type = changes.type[changes.type.length - 1];
            this.filters.type = [type];
        }

        if (
            changes[this.accessibilityFiltersService.getResponsibilityTranslation()] &&
            changes[this.accessibilityFiltersService.getResponsibilityTranslation()].length > 0
        ) {
            const length = this.filters[this.accessibilityFiltersService.getResponsibilityTranslation()].length;
            this.filters[this.accessibilityFiltersService.getResponsibilityTranslation()] = [
                this.filters[this.accessibilityFiltersService.getResponsibilityTranslation()][length - 1],
            ];
        }
        this.refreshChecks.emit();
    }

    setupTabs (): void {
        this.tabOptions = [{ name: this.translateService.getString('All levels', {}), value: '' }];
        let accVersion = this.sessionService.domain?.features?.accessibility;
        if (!accVersion) {
            return;
        }

        const indexOfVersion = accVersion.search(/_v(\d+)?/);
        if (indexOfVersion > -1) {
            accVersion = accVersion.slice(0, indexOfVersion);
        }
        switch (accVersion) {
            case 'WCAG2-AAA':
                this.tabOptions = this.tabOptions.concat([
                    { name: this.translateService.getString('Level A', {}), value: 'A' },
                    { name: this.translateService.getString('Level AA', {}), value: 'AA' },
                    { name: this.translateService.getString('Level AAA', {}), value: 'AAA' },
                ]);
                break;

            case 'WCAG2-AA':
                this.tabOptions = this.tabOptions.concat([
                    { name: this.translateService.getString('Level A', {}), value: 'A' },
                    { name: this.translateService.getString('Level AA', {}), value: 'AA' },
                ]);
                break;
            case 'WCAG2-A':
                this.tabOptions = this.tabOptions.concat([{ name: this.translateService.getString('Level A', {}), value: 'A' }]);
                break;
        }
    }

    onTabChange (tab: string): void {
        this.tabChange.emit(tab);
        this.selectAvailableCheck();
    }

    handleTabChange (event: any): void {
        const tab = event.tab || event;
        this.onTabChange(tab);
    }

    onSelectCheck (check: any): void {
        this.check = check;
        this.check.errors = [];
        this.getErrors(false); // Shouldn't refresh page when getting errors
    }

    getErrors (update: boolean): void {
        // Check if only the errors should be updated, or the check
        if (update) {
            this.refreshChecks.emit();
            this.selectAvailableCheck();
        }
        this.errorLoading = true;
        const params = {
            page_size: 0,
        };
        this.errorProgress = this.pageAccessibilityRepo.getAllCheckErrors(this.page.id, this.check.id, params)
            .then((data) => {
                this.check.errors = data;
                this.check.errors_count = data.filter((error: any) => {
                    return !error.ignored;
                }).length;
            }, () => {})
            .finally(() => {
                this.errorLoading = false;
            });
    }

    selectAvailableCheck (): void {
        setTimeout(() => {
            Promise.resolve(this.progress).then(() => {
                setTimeout(() => {
                    const checks = this.getChecks().filter((checkItem: any) => {
                        if (this.tab === 'passed_checks') {
                            return checkItem.errors_count === 0 && !checkItem.ignored;
                        } else if (this.tab === 'ignored_issues') {
                            return checkItem.ignored;
                        } else {
                            return checkItem.errors_count > 0;
                        }
                    }); // Use same filtering methods as the tables component
                    let selectedCheck;
                    if (typeof this.check === 'object') {
                        // Look for currently selected check
                        selectedCheck = checks.find((checkItem: any) => {
                            return this.check.id === checkItem.id;
                        });
                    }
                    if (typeof selectedCheck === 'undefined' && checks.length > 0) {
                        // If no check is found, select the first available check
                        selectedCheck = checks[0];
                    }
                    if (typeof selectedCheck === 'object') {
                        // Only fetch errors if a check was found
                        this.onSelectCheck(selectedCheck);
                    }
                });
            }, () => {});
        });
    }

    getChecks (): any[] {
        let collection: any[] = [];
        if (this.issues) {
            if (this.issues.A) {
                collection = collection.concat(this.issues.A);
            }
            if (this.issues.AA) {
                collection = collection.concat(this.issues.AA);
            }
            if (this.issues.AAA) {
                collection = collection.concat(this.issues.AAA);
            }
        }
        return collection;
    }
}
